import type {
  ReportingScopeField,
  ReportingScopeFilter,
} from "@/types/reporting";

export interface ReportingScopeInput {
  advertiserIds: number[];
  agencyIds: number[];
  agencyUser: boolean;
  campaignIds: number[];
  organizationId: number;
  selectedAdvertiserIds: number[];
  selectedAgencyIds: number[];
  selectedCampaignIds: number[];
}

interface LevelSelection {
  field: ReportingScopeField;
  kept: number[];
  removed: number[];
}

const ascending = (first: number, second: number) => first - second;

// Splits a level's available ids into the ones the user kept and the ones they
// removed, in one pass — the campaign list can run to thousands.
const splitSelection = (
  available: number[],
  selected: number[]
): Omit<LevelSelection, "field"> => {
  const selectedSet = new Set(selected);
  return available.reduce<Omit<LevelSelection, "field">>(
    (acc, id) => {
      (selectedSet.has(id) ? acc.kept : acc.removed).push(id);
      return acc;
    },
    { kept: [], removed: [] }
  );
};

const include = (
  field: ReportingScopeField,
  ids: number[]
): ReportingScopeFilter => ({ field, value: ids.toSorted(ascending) });

// An include fully defines the scope, so it replaces every filter above it.
// The Reporting API only accepts `=` (an array means "contains") on entity id
// fields, so a level with anything removed can only be described by the ids
// that were kept — never by excluding the removed ones.
const applyLevels = (
  rootFilters: ReportingScopeFilter[],
  levels: LevelSelection[]
): ReportingScopeFilter[] =>
  levels.reduce(
    (acc, { field, kept, removed }) =>
      removed.length === 0 ? acc : [include(field, kept)],
    rootFilters
  );

/**
 * Builds the Reporting API filters for the user's hierarchy selection, sending
 * a parent id in place of its children wherever the whole subtree is selected.
 *
 * Walks down from the top of the hierarchy. A level where everything is kept
 * adds nothing; its parent's filter already covers it. A level with anything
 * removed sends the ids that were kept at that level instead, since the
 * Reporting API rejects `!=` on entity ids.
 *
 * Infillion users start from the organization plus agency_status = true, so
 * inactive agencies stay out, matching what the agency picker lists. Agency
 * users start from their own agencies.
 *
 * @returns the filters, or null when nothing is selected at some level and
 *   there is nothing to fetch
 */
export const buildReportingScopeFilters = ({
  advertiserIds,
  agencyIds,
  agencyUser,
  campaignIds,
  organizationId,
  selectedAdvertiserIds,
  selectedAgencyIds,
  selectedCampaignIds,
}: ReportingScopeInput): ReportingScopeFilter[] | null => {
  const agencies = splitSelection(agencyIds, selectedAgencyIds);
  const advertiserLevel: LevelSelection = {
    field: "advertiser_id",
    ...splitSelection(advertiserIds, selectedAdvertiserIds),
  };
  const campaignLevel: LevelSelection = {
    field: "campaign_id",
    ...splitSelection(campaignIds, selectedCampaignIds),
  };

  if (
    agencies.kept.length === 0 ||
    advertiserLevel.kept.length === 0 ||
    campaignLevel.kept.length === 0
  ) {
    return null;
  }

  // An agency user's organization also holds agencies they cannot see.
  if (agencyUser) {
    return applyLevels(
      [include("agency_id", agencies.kept)],
      [advertiserLevel, campaignLevel]
    );
  }
  const agencyLevel: LevelSelection = { field: "agency_id", ...agencies };
  return applyLevels(
    [
      include("organization_id", [organizationId]),
      { field: "agency_status", value: true },
    ],
    [agencyLevel, advertiserLevel, campaignLevel]
  );
};
